import StepCard from '../../../../components/wizard/StepCard';
import { useTranslation } from 'react-i18next';
import { WizardData } from '../../../../types/wizard';

interface Step7MustDoExperiencesProps {
  data: WizardData;
  onChange: (field: keyof WizardData, value: any) => void;
  error?: string;
}

const experiences = [
  {
    id: 'gastronomia',
    label: 'Gastronomia Local',
    icon: 'ri-restaurant-2-line',
    description: 'Restaurantes autênticos, mercados e experiências culinárias'
  },
  {
    id: 'cultura',
    label: 'Cultura e História',
    icon: 'ri-building-line',
    description: 'Museus, monumentos históricos e patrimônios culturais'
  },
  {
    id: 'natureza',
    label: 'Natureza e Paisagens',
    icon: 'ri-landscape-line',
    description: 'Parques nacionais, trilhas, praias e cenários naturais'
  },
  {
    id: 'aventura',
    label: 'Aventura e Esportes',
    icon: 'ri-riding-line',
    description: 'Atividades radicais, trekking, mergulho e esportes'
  },
  {
    id: 'vida_noturna',
    label: 'Vida Noturna',
    icon: 'ri-goblet-line',
    description: 'Bares, clubes, shows e entretenimento noturno'
  },
  {
    id: 'compras',
    label: 'Compras',
    icon: 'ri-shopping-bag-3-line',
    description: 'Mercados locais, boutiques e centros comerciais'
  },
  {
    id: 'relaxamento',
    label: 'Relaxamento e Bem-estar',
    icon: 'ri-heart-pulse-line',
    description: 'Spas, praias tranquilas e momentos de descanso'
  },
  {
    id: 'fotografia',
    label: 'Fotografia e Mirantes',
    icon: 'ri-camera-line',
    description: 'Pontos panorâmicos e locais instagramáveis'
  },
  {
    id: 'arte',
    label: 'Arte e Design',
    icon: 'ri-palette-line',
    description: 'Galerias, street art e arquitetura contemporânea'
  },
  {
    id: 'espiritualidade',
    label: 'Espiritualidade',
    icon: 'ri-ancient-pavilion-line',
    description: 'Templos, retiros e experiências espirituais'
  },
  {
    id: 'interacao_local',
    label: 'Interação com Locais',
    icon: 'ri-group-line',
    description: 'Experiências comunitárias e convivência autêntica'
  },
  {
    id: 'vinhos',
    label: 'Vinhos e Degustações',
    icon: 'ri-goblet-2-line',
    description: 'Vinícolas, cervejarias e experiências gastronômicas'
  }
];

export default function Step7MustDoExperiences({ data, onChange, error }: Step7MustDoExperiencesProps) {
  const { i18n } = useTranslation();
  const isEnglish = i18n.resolvedLanguage?.startsWith('en');
  const selectedExperiences = data.mustDoExperiences || [];
  const localizedExperiences = experiences.map((exp) => ({
    ...exp,
    label:
      isEnglish
        ? ({
            gastronomia: 'Local Food',
            cultura: 'Culture & History',
            natureza: 'Nature & Landscapes',
            aventura: 'Adventure & Sports',
            vida_noturna: 'Nightlife',
            compras: 'Shopping',
            relaxamento: 'Relaxation & Wellness',
            fotografia: 'Photography & Viewpoints',
            arte: 'Art & Design',
            espiritualidade: 'Spirituality',
            interacao_local: 'Local Interaction',
            vinhos: 'Wine & Tastings',
          } as Record<string, string>)[exp.id]
        : exp.label,
    description:
      isEnglish
        ? ({
            gastronomia: 'Authentic restaurants, markets and culinary experiences',
            cultura: 'Museums, historic monuments and cultural heritage',
            natureza: 'National parks, trails, beaches and natural scenery',
            aventura: 'Adrenaline activities, trekking, diving and sports',
            vida_noturna: 'Bars, clubs, shows and evening entertainment',
            compras: 'Local markets, boutiques and shopping districts',
            relaxamento: 'Spas, quiet beaches and moments to slow down',
            fotografia: 'Scenic viewpoints and photogenic locations',
            arte: 'Galleries, street art and contemporary architecture',
            espiritualidade: 'Temples, retreats and spiritual experiences',
            interacao_local: 'Community experiences and authentic local connection',
            vinhos: 'Wineries, breweries and tasting experiences',
          } as Record<string, string>)[exp.id]
        : exp.description,
  }));

  const handleToggle = (experienceId: string) => {
    const isSelected = selectedExperiences.includes(experienceId);
    const updated = isSelected
      ? selectedExperiences.filter(id => id !== experienceId)
      : [...selectedExperiences, experienceId];
    onChange('mustDoExperiences', updated);
  };

  return (
    <StepCard
      title={isEnglish ? 'Must-Have Experiences' : 'Experiências Obrigatórias'}
      description={isEnglish
        ? 'Select the experiences you consider essential for your trip. Choose at least 3 options.'
        : 'Selecione as experiências que você considera essenciais para sua viagem. Escolha pelo menos 3 opções.'}
    >
      <div className="space-y-6">
        {/* Counter */}
        <div className="flex items-center justify-between p-4 bg-white/5 border border-white/10 rounded-lg">
          <span className="text-sm text-white/90">{isEnglish ? 'Selected experiences' : 'Experiências selecionadas'}</span>
          <span className="text-lg font-semibold text-[#FFD700]">
            {selectedExperiences.length}
          </span>
        </div>

        {/* Experience Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {localizedExperiences.map((exp) => {
            const isSelected = selectedExperiences.includes(exp.id);
            return (
              <button
                key={exp.id}
                type="button"
                onClick={() => handleToggle(exp.id)}
                className={`
                  relative p-4 rounded-xl border-2 transition-all duration-200 text-left
                  ${isSelected
                    ? 'bg-[#FFD700]/10 border-[#FFD700] shadow-lg shadow-[#FFD700]/20'
                    : 'bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20'
                  }
                `}
              >
                {/* Check Icon */}
                {isSelected && (
                  <div className="absolute top-3 right-3 w-6 h-6 flex items-center justify-center bg-[#FFD700] rounded-full">
                    <i className="ri-check-line text-sm text-white"></i>
                  </div>
                )}

                {/* Icon */}
                <div className={`
                  w-12 h-12 flex items-center justify-center rounded-lg mb-3
                  ${isSelected ? 'bg-[#FFD700]/20' : 'bg-white/5'}
                `}>
                  <i className={`${exp.icon} text-2xl ${isSelected ? 'text-[#FFD700]' : 'text-white/90'}`}></i>
                </div>

                {/* Label */}
                <h3 className={`text-sm font-semibold mb-1 ${isSelected ? 'text-white' : 'text-white/90'}`}>
                  {exp.label}
                </h3>

                {/* Description */}
                <p className="text-xs text-white/90 leading-relaxed">
                  {exp.description}
                </p>
              </button>
            );
          })}
        </div>

        {/* Error Message */}
        {error && (
          <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
            <i className="ri-error-warning-line text-red-400"></i>
            <span className="text-sm text-red-400">{error}</span>
          </div>
        )}

        {/* Info Box */}
        <div className="p-4 bg-[#FFD700]/5 border border-[#FFD700]/20 rounded-lg">
          <div className="flex gap-3">
            <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
              <i className="ri-lightbulb-line text-[#FFD700]"></i>
            </div>
            <div className="text-xs text-white/90 leading-relaxed">
              <strong className="text-white">{isEnglish ? 'Tip:' : 'Dica:'}</strong>{' '}
              {isEnglish
                ? 'Select the experiences that truly matter to you. This helps us prioritize activities and shape an itinerary aligned with your expectations.'
                : 'Selecione as experiências que realmente fazem sentido para você. Isso nos ajuda a priorizar atividades e criar um roteiro alinhado com suas expectativas.'}
            </div>
          </div>
        </div>
      </div>
    </StepCard>
  );
}
